import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';
import { authService } from '../services/auth-service.js';

const API_BASE = '/voice/php/api';

export class PhoneNumbers extends LitElement {
  static properties = {
    selectedNumber: { type: Object },
    numbers: { type: Array },
    _loading: { type: Boolean, state: true },
    _searchQuery: { type: String, state: true },
    _sortColumn: { type: String, state: true },
    _sortDirection: { type: String, state: true },
    _currentPage: { type: Number, state: true },
    _pageSize: { type: Number, state: true },
    _totalItems: { type: Number, state: true },
    _totalPages: { type: Number, state: true },
    _showModal: { type: Boolean, state: true },
    _modalMode: { type: String, state: true },
    _editingNumber: { type: Object, state: true },
    _saving: { type: Boolean, state: true },
    _toastVisible: { type: Boolean, state: true },
    _toastMessage: { type: String, state: true },
    _toastType: { type: String, state: true },
    _confirmDelete: { type: Object, state: true }
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
      }

      /* ─── Page Header ─── */
      .page-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .page-header-text {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .page-description {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      /* ─── Stats Row ─── */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-4);
        margin-bottom: var(--space-8);
      }

      .stat-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        display: flex;
        align-items: center;
        gap: var(--space-4);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .stat-icon {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.125rem;
        flex-shrink: 0;
      }

      .stat-icon.total {
        background: var(--color-primary-light);
        color: var(--color-primary);
      }

      .stat-icon.active {
        background: var(--color-success-light);
        color: var(--color-success);
      }

      .stat-icon.cost {
        background: var(--color-accent-light);
        color: var(--color-accent);
      }

      .stat-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      /* ─── Table Container ─── */
      .table-container {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
      }

      .table-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4) var(--space-5);
        border-bottom: 1px solid var(--color-border-primary);
      }

      .table-title {
        font-family: var(--font-display);
        font-size: var(--text-base);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }

      .table-search {
        position: relative;
      }

      .table-search .input {
        width: 240px;
        padding-left: var(--space-8);
      }

      .table-search-icon {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
        pointer-events: none;
      }

      .table-scroll {
        overflow-x: auto;
      }

      /* ─── Table Customizations ─── */
      .data-table thead th:first-child {
        border-radius: 0;
      }

      .data-table thead th:last-child {
        border-radius: 0;
      }

      .phone-cell {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .phone-flag {
        font-size: var(--text-lg);
        line-height: 1;
      }

      .phone-number {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        letter-spacing: 0.02em;
      }

      .agent-cell {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .agent-mini-avatar {
        width: 24px;
        height: 24px;
        border-radius: var(--radius-full);
        background: linear-gradient(135deg, var(--color-primary-subtle), var(--color-primary-light));
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-xs);
        color: var(--color-primary);
        font-weight: var(--font-semibold);
        flex-shrink: 0;
      }

      .agent-name-text {
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .label-text {
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
      }

      .park-text {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        background: var(--color-bg-tertiary);
        padding: 2px 6px;
        border-radius: var(--radius-sm);
      }

      .cost-text {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .actions-cell {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .action-btn {
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
      }

      .action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .action-btn.danger:hover {
        background: var(--color-error-light);
        color: var(--color-error);
      }

      /* ─── Clickable Row ─── */
      .data-table tbody tr {
        cursor: pointer;
      }

      .data-table tbody tr.expanded {
        background: var(--color-bg-hover);
      }

      .data-table tbody tr.expanded td {
        border-bottom-color: transparent;
      }

      /* ─── Expanded Row Detail ─── */
      .expand-row td {
        padding: 0 !important;
        border-bottom: 1px solid var(--color-border-primary) !important;
      }

      .expand-content {
        padding: var(--space-4) var(--space-5);
        padding-left: calc(var(--space-5) + var(--space-8));
        background: var(--color-bg-tertiary);
        animation: slideDown var(--duration-base) var(--ease-out) both;
      }

      .expand-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-5);
      }

      .expand-section {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .expand-section-title {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      .expand-section-value {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-primary);
        line-height: var(--leading-relaxed);
      }

      .hours-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .hours-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .hours-day {
        width: 32px;
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        font-size: var(--text-xs);
        text-transform: uppercase;
      }

      .hours-time {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
      }

      .notes-textarea {
        width: 100%;
        min-height: 60px;
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        resize: vertical;
        transition: all var(--transition-fast);
        line-height: var(--leading-normal);
      }

      .notes-textarea:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .caller-id-row {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .caller-id-row i {
        color: var(--color-success);
        font-size: var(--text-xs);
      }

      /* ─── Sort indicator ─── */
      .sortable {
        cursor: pointer;
        user-select: none;
      }
      .sortable:hover {
        color: var(--color-text-primary);
      }
      .sort-icon {
        margin-left: 4px;
        font-size: var(--text-xs);
        opacity: 0.5;
      }
      .sort-icon.active {
        opacity: 1;
        color: var(--color-accent);
      }

      /* ─── Loading / Empty ─── */
      .loading-row td, .empty-row td {
        text-align: center;
        padding: var(--space-8) !important;
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
      }

      /* ─── Modal Overlay ─── */
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        animation: fadeIn var(--duration-base) var(--ease-out) both;
      }

      .modal {
        background: var(--color-bg-primary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-xl);
        width: 580px;
        max-width: 95vw;
        max-height: 85vh;
        overflow-y: auto;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        animation: slideUp var(--duration-base) var(--ease-out) both;
      }

      .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-5) var(--space-6);
        border-bottom: 1px solid var(--color-border-primary);
      }

      .modal-title {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
      }

      .modal-close {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        border-radius: var(--radius-md);
        cursor: pointer;
        font-size: var(--text-base);
      }
      .modal-close:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .modal-body {
        padding: var(--space-6);
        display: flex;
        flex-direction: column;
        gap: var(--space-5);
      }

      .modal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--space-3);
        padding: var(--space-4) var(--space-6);
        border-top: 1px solid var(--color-border-primary);
      }

      .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-4);
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .form-group.full {
        grid-column: 1 / -1;
      }

      .form-label {
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-secondary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      .form-input, .form-select, .form-textarea {
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        transition: border-color var(--transition-fast);
      }

      .form-input:focus, .form-select:focus, .form-textarea:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .form-textarea {
        min-height: 60px;
        resize: vertical;
      }

      .form-hint {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .form-section-title {
        font-size: var(--text-xs);
        font-weight: var(--font-bold);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
        padding-top: var(--space-2);
        border-top: 1px solid var(--color-border-primary);
      }

      .toggle-row {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .toggle {
        width: 40px;
        height: 22px;
        border-radius: 11px;
        background: var(--color-bg-tertiary);
        border: 1px solid var(--color-border-primary);
        cursor: pointer;
        position: relative;
        transition: all var(--transition-fast);
        flex-shrink: 0;
      }
      .toggle::after {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: white;
        top: 2px;
        left: 2px;
        transition: transform var(--transition-fast);
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      }
      .toggle.active {
        background: var(--color-primary);
        border-color: var(--color-primary);
      }
      .toggle.active::after {
        transform: translateX(18px);
      }

      /* ─── Delete Confirm ─── */
      .confirm-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1001;
      }

      .confirm-box {
        background: var(--color-bg-primary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        width: 400px;
        max-width: 90vw;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        text-align: center;
      }

      .confirm-icon {
        width: 48px;
        height: 48px;
        border-radius: var(--radius-full);
        background: var(--color-error-light);
        color: var(--color-error);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-xl);
        margin: 0 auto var(--space-4);
      }

      .confirm-title {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        margin-bottom: var(--space-2);
      }

      .confirm-message {
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        margin-bottom: var(--space-5);
        line-height: var(--leading-relaxed);
      }

      .confirm-actions {
        display: flex;
        gap: var(--space-3);
        justify-content: center;
      }

      .btn-danger {
        background: var(--color-error);
        color: white;
        border: none;
        padding: var(--space-2) var(--space-4);
        border-radius: var(--radius-md);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        cursor: pointer;
      }
      .btn-danger:hover {
        opacity: 0.9;
      }

      .btn-cancel {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
        border: 1px solid var(--color-border-primary);
        padding: var(--space-2) var(--space-4);
        border-radius: var(--radius-md);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        cursor: pointer;
      }
      .btn-cancel:hover {
        background: var(--color-bg-hover);
      }

      /* ─── Toast ─── */
      .toast {
        position: fixed;
        bottom: var(--space-6);
        right: var(--space-6);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-lg);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: white;
        z-index: 1100;
        animation: slideUp var(--duration-base) var(--ease-out) both;
        box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      .toast.success { background: var(--color-success); }
      .toast.error { background: var(--color-error); }

      /* ─── Pagination ─── */
      .pagination-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-top: 1px solid var(--color-border-primary);
        background: var(--color-bg-tertiary);
      }

      .pagination-info {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .pagination-info strong {
        color: var(--color-text-secondary);
        font-weight: var(--font-semibold);
      }

      .pagination-controls {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .page-btn {
        min-width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 var(--space-2);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .page-btn:hover:not(:disabled) {
        background: var(--color-bg-tertiary);
        border-color: var(--color-border-secondary);
        color: var(--color-text-primary);
      }

      .page-btn.active {
        background: var(--color-primary);
        border-color: var(--color-primary);
        color: var(--color-text-inverse);
      }

      .page-btn:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }

      .page-ellipsis {
        min-width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
      }

      /* ─── Responsive ─── */
      @media (max-width: 1024px) {
        .stats-row {
          grid-template-columns: 1fr;
        }

        .expand-grid {
          grid-template-columns: 1fr;
        }

        .page-header {
          flex-direction: column;
          gap: var(--space-3);
        }

        .form-row {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 768px) {
        .table-header {
          flex-direction: column;
          gap: var(--space-3);
          align-items: stretch;
        }

        .table-search .input {
          width: 100%;
        }
      }
    `
  ];

  constructor() {
    super();
    this.selectedNumber = null;
    this.numbers = [];
    this._loading = true;
    this._searchQuery = '';
    this._sortColumn = '';
    this._sortDirection = 'asc';
    this._currentPage = 1;
    this._pageSize = 20;
    this._totalItems = 0;
    this._totalPages = 1;
    this._showModal = false;
    this._modalMode = 'add';
    this._editingNumber = {};
    this._saving = false;
    this._toastVisible = false;
    this._toastMessage = '';
    this._toastType = 'success';
    this._confirmDelete = null;
    this._searchDebounceTimer = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadNumbers();
  }

  /* ─── API Helpers ─── */

  _getHeaders(includeBody = false) {
    const headers = { 'Authorization': `Bearer ${authService.getToken()}` };
    if (includeBody) headers['Content-Type'] = 'application/json';
    return headers;
  }

  async _apiCall(method, body = null, query = '') {
    const url = `${API_BASE}/phone-numbers.php${query}`;
    const opts = { method, headers: this._getHeaders(!!body) };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(url, opts);
    return res.json();
  }

  /* ─── Data Loading ─── */

  async _loadNumbers() {
    this._loading = true;
    try {
      const params = new URLSearchParams({
        page: this._currentPage,
        limit: this._pageSize
      });
      if (this._searchQuery) params.set('search', this._searchQuery);
      if (this._sortColumn) {
        // Map frontend column names to DB column names
        const sortMap = { phone: 'twilio_number', label: 'label', agent: 'agent_name', status: 'status', cost: 'monthly_cost' };
        params.set('sort', sortMap[this._sortColumn] || this._sortColumn);
        params.set('direction', this._sortDirection);
      }

      const data = await this._apiCall('GET', null, `?${params.toString()}`);
      if (data.success) {
        this.numbers = data.numbers.map(n => this._formatForUI(n));
        if (data.pagination) {
          this._currentPage = data.pagination.page;
          this._totalItems = data.pagination.total;
          this._totalPages = data.pagination.pages;
        }
      } else {
        this._showToast(data.error || 'Failed to load numbers', 'error');
      }
    } catch (err) {
      console.error('[PhoneNumbers] Load error:', err);
      this._showToast('Network error loading numbers', 'error');
    } finally {
      this._loading = false;
    }
  }

  /* ─── Format API → UI ─── */

  _formatForUI(n) {
    return {
      id: n.id,
      phone: this._formatPhone(n.twilio_number),
      raw: n.twilio_number,
      country: n.country_code || 'US',
      flag: this._getFlag(n.country_code || 'US'),
      type: n.number_type || 'Local',
      label: n.label || '',
      agent: {
        name: n.agent_name || 'Unassigned',
        initials: this._getInitials(n.agent_name)
      },
      ujid: n.ujid || 0,
      park: n.ujid ? `UJID-${n.ujid}` : '',
      parkName: n.park_name || '',
      status: n.status || 'inactive',
      monthlyCost: n.monthly_cost || 0,
      callerIdName: n.caller_id_name || '',
      callerIdEnabled: !!n.caller_id_enabled,
      notes: n.notes || '',
      inboundHours: this._formatHoursForUI(n.business_hours),
      businessHoursRaw: n.business_hours || {},
      timezone: n.timezone || 'America/New_York',
      routingRules: n.routing_rules || '',
      createdAt: n.created_at,
      updatedAt: n.updated_at
    };
  }

  _formatPhone(raw) {
    if (!raw) return '';
    // US/CA: +1XXXXXXXXXX → +1 (XXX) XXX-XXXX
    const match = raw.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
    if (match) return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
    return raw;
  }

  _getFlag(code) {
    const flags = { US: '\u{1F1FA}\u{1F1F8}', CA: '\u{1F1E8}\u{1F1E6}', GB: '\u{1F1EC}\u{1F1E7}', MX: '\u{1F1F2}\u{1F1FD}' };
    return flags[code] || '\u{1F30E}';
  }

  _getInitials(name) {
    if (!name) return '--';
    const parts = name.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  _formatHoursForUI(bh) {
    if (!bh || typeof bh !== 'object' || Object.keys(bh).length === 0) return [];
    const dayMap = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const result = [];
    for (const day of dayOrder) {
      if (!bh[day]) continue;
      const d = bh[day];
      if (d.closed) {
        result.push({ day: dayMap[day], time: 'Closed' });
      } else if (d.open === '00:00' && d.close === '23:59') {
        result.push({ day: dayMap[day], time: '24 hours' });
      } else {
        result.push({ day: dayMap[day], time: `${this._to12h(d.open)} - ${this._to12h(d.close)}` });
      }
    }
    return result;
  }

  _to12h(time24) {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  /* ─── Search & Sort (server-side) ─── */

  _handleSearch(e) {
    this._searchQuery = e.target.value;
    // Debounce: wait 350ms after user stops typing
    clearTimeout(this._searchDebounceTimer);
    this._searchDebounceTimer = setTimeout(() => {
      this._currentPage = 1;
      this._loadNumbers();
    }, 350);
  }

  _sortBy(column) {
    if (this._sortColumn === column) {
      this._sortDirection = this._sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this._sortColumn = column;
      this._sortDirection = 'asc';
    }
    this._currentPage = 1;
    this._loadNumbers();
  }

  _sortIcon(column) {
    if (this._sortColumn !== column) return '';
    return this._sortDirection === 'asc' ? '\u25B2' : '\u25BC';
  }

  /* ─── Pagination ─── */

  _goToPage(p) {
    const page = Math.max(1, Math.min(p, this._totalPages));
    if (page !== this._currentPage) {
      this._currentPage = page;
      this._loadNumbers();
    }
  }

  _renderPageNumbers() {
    const total = this._totalPages;
    const current = this._currentPage;
    const pages = [];
    const range = 2;

    pages.push(1);

    if (current - range > 2) {
      pages.push('...');
    }

    for (let i = Math.max(2, current - range); i <= Math.min(total - 1, current + range); i++) {
      pages.push(i);
    }

    if (current + range < total - 1) {
      pages.push('...');
    }

    if (total > 1) {
      pages.push(total);
    }

    return pages.map(p =>
      p === '...'
        ? html`<span class="page-ellipsis">...</span>`
        : html`
          <button
            class="page-btn ${p === current ? 'active' : ''}"
            @click="${() => this._goToPage(p)}"
          >${p}</button>
        `
    );
  }

  /* ─── Row Expand ─── */

  _toggleExpand(number) {
    if (this.selectedNumber?.id === number.id) {
      this.selectedNumber = null;
    } else {
      this.selectedNumber = number;
    }
  }

  /* ─── Stats ─── */

  _getActiveCount() {
    return this.numbers.filter(n => n.status === 'active').length;
  }

  _getTotalCost() {
    return this.numbers.reduce((sum, n) => sum + n.monthlyCost, 0).toFixed(2);
  }

  /* ─── Add / Edit Modal ─── */

  _openAddModal() {
    this._modalMode = 'add';
    this._editingNumber = {
      twilio_number: '',
      label: '',
      number_type: 'Local',
      country_code: 'US',
      ujid: '',
      park_name: '',
      agent_name: '',
      status: 'inactive',
      monthly_cost: '',
      caller_id_name: '',
      caller_id_enabled: false,
      timezone: 'America/New_York',
      routing_rules: '',
      notes: ''
    };
    this._showModal = true;
  }

  _openEditModal(num) {
    this._modalMode = 'edit';
    this._editingNumber = {
      id: num.id,
      twilio_number: num.raw,
      label: num.label,
      number_type: num.type,
      country_code: num.country,
      ujid: num.ujid || '',
      park_name: num.parkName,
      agent_name: num.agent.name === 'Unassigned' ? '' : num.agent.name,
      status: num.status,
      monthly_cost: num.monthlyCost || '',
      caller_id_name: num.callerIdName,
      caller_id_enabled: num.callerIdEnabled,
      timezone: num.timezone,
      routing_rules: num.routingRules,
      notes: num.notes
    };
    this._showModal = true;
  }

  _closeModal() {
    this._showModal = false;
    this._editingNumber = {};
  }

  _updateField(field, value) {
    this._editingNumber = { ...this._editingNumber, [field]: value };
  }

  async _saveModal() {
    if (this._saving) return;

    const data = this._editingNumber;
    if (!data.twilio_number) {
      this._showToast('Phone number is required', 'error');
      return;
    }

    // Clean up - convert empty strings to proper types
    const payload = { ...data };
    if (payload.ujid === '') payload.ujid = 0;
    else payload.ujid = parseInt(payload.ujid, 10) || 0;
    if (payload.monthly_cost === '') payload.monthly_cost = 0;
    else payload.monthly_cost = parseFloat(payload.monthly_cost) || 0;
    payload.caller_id_enabled = !!payload.caller_id_enabled;

    this._saving = true;
    try {
      const method = this._modalMode === 'add' ? 'POST' : 'PUT';
      const result = await this._apiCall(method, payload);

      if (result.success) {
        this._showToast(this._modalMode === 'add' ? 'Phone number added' : 'Phone number updated');
        this._closeModal();
        await this._loadNumbers();
      } else {
        this._showToast(result.error || 'Save failed', 'error');
      }
    } catch (err) {
      this._showToast('Network error saving', 'error');
    } finally {
      this._saving = false;
    }
  }

  /* ─── Delete ─── */

  _confirmDeleteNumber(num) {
    this._confirmDelete = num;
  }

  _cancelDelete() {
    this._confirmDelete = null;
  }

  async _executeDelete() {
    const num = this._confirmDelete;
    if (!num) return;

    try {
      const result = await this._apiCall('DELETE', null, `?id=${num.id}`);
      if (result.success) {
        this._showToast('Phone number deleted');
        this._confirmDelete = null;
        if (this.selectedNumber?.id === num.id) this.selectedNumber = null;
        await this._loadNumbers();
      } else {
        this._showToast(result.error || 'Delete failed', 'error');
      }
    } catch (err) {
      this._showToast('Network error deleting', 'error');
    }
  }

  /* ─── Notes Auto-save ─── */

  async _saveNotes(num, e) {
    const newNotes = e.target.value;
    if (newNotes === num.notes) return;

    try {
      const result = await this._apiCall('PUT', { id: num.id, notes: newNotes });
      if (result.success) {
        // Update local data
        const idx = this.numbers.findIndex(n => n.id === num.id);
        if (idx >= 0) {
          this.numbers = this.numbers.map(n => n.id === num.id ? { ...n, notes: newNotes } : n);
          if (this.selectedNumber?.id === num.id) {
            this.selectedNumber = { ...this.selectedNumber, notes: newNotes };
          }
        }
      }
    } catch (err) {
      console.error('[PhoneNumbers] Notes save error:', err);
    }
  }

  /* ─── Toast ─── */

  _showToast(message, type = 'success') {
    this._toastMessage = message;
    this._toastType = type;
    this._toastVisible = true;
    setTimeout(() => { this._toastVisible = false; }, 4000);
  }

  /* ─── Render ─── */

  render() {
    const startIdx = (this._currentPage - 1) * this._pageSize;
    const endIdx = Math.min(startIdx + this.numbers.length, this._totalItems);

    return html`
      ${iconStyles}

      <!-- Page Header -->
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Phone Numbers</h1>
          <p class="page-description">Manage your Twilio phone numbers and routing configuration</p>
        </div>
        <button class="btn btn-primary" @click=${this._openAddModal}>
          <i class="fa-sharp fa-light fa-plus"></i>
          Add Number
        </button>
      </div>

      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-icon total">
            <i class="fa-sharp-duotone fa-solid fa-phone-office"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">${this._totalItems}</span>
            <span class="metric-label">Total Numbers</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon active">
            <i class="fa-sharp-duotone fa-solid fa-circle-check"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">${this._getActiveCount()}</span>
            <span class="metric-label">Active Numbers</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon cost">
            <i class="fa-sharp-duotone fa-solid fa-dollar-sign"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">$${this._getTotalCost()}</span>
            <span class="metric-label">Monthly Cost</span>
          </div>
        </div>
      </div>

      <!-- Numbers Table -->
      <div class="table-container">
        <div class="table-header">
          <span class="table-title">All Numbers</span>
          <div class="table-search">
            <i class="fa-sharp fa-light fa-magnifying-glass table-search-icon"></i>
            <input type="text" class="input" placeholder="Search numbers..." .value=${this._searchQuery} @input=${this._handleSearch} />
          </div>
        </div>

        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th class="sortable" @click=${() => this._sortBy('phone')}>
                  Phone Number
                  <span class="sort-icon ${this._sortColumn === 'phone' ? 'active' : ''}">${this._sortIcon('phone')}</span>
                </th>
                <th class="sortable" @click=${() => this._sortBy('label')}>
                  Label / Name
                  <span class="sort-icon ${this._sortColumn === 'label' ? 'active' : ''}">${this._sortIcon('label')}</span>
                </th>
                <th class="sortable" @click=${() => this._sortBy('agent')}>
                  Assigned Agent
                  <span class="sort-icon ${this._sortColumn === 'agent' ? 'active' : ''}">${this._sortIcon('agent')}</span>
                </th>
                <th>Park (UJID)</th>
                <th class="sortable" @click=${() => this._sortBy('status')}>
                  Status
                  <span class="sort-icon ${this._sortColumn === 'status' ? 'active' : ''}">${this._sortIcon('status')}</span>
                </th>
                <th class="sortable" @click=${() => this._sortBy('cost')}>
                  Monthly Cost
                  <span class="sort-icon ${this._sortColumn === 'cost' ? 'active' : ''}">${this._sortIcon('cost')}</span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${this._loading ? html`
                <tr class="loading-row"><td colspan="7">Loading phone numbers...</td></tr>
              ` : this.numbers.length === 0 ? html`
                <tr class="empty-row"><td colspan="7">
                  ${this._searchQuery ? 'No numbers match your search' : 'No phone numbers configured yet'}
                </td></tr>
              ` : this.numbers.map(num => html`
                <tr
                  class="${this.selectedNumber?.id === num.id ? 'expanded' : ''}"
                  @click="${() => this._toggleExpand(num)}"
                >
                  <td>
                    <div class="phone-cell">
                      <span class="phone-flag">${num.flag}</span>
                      <span class="phone-number">${num.phone}</span>
                    </div>
                  </td>
                  <td>
                    <span class="label-text">${num.label}</span>
                  </td>
                  <td>
                    <div class="agent-cell">
                      <div class="agent-mini-avatar">${num.agent.initials}</div>
                      <span class="agent-name-text">${num.agent.name}</span>
                    </div>
                  </td>
                  <td>
                    <span class="park-text">${num.park}</span>
                  </td>
                  <td>
                    <span class="badge ${num.status === 'active' ? 'badge-success' : 'badge-neutral'}">
                      <span class="status-dot ${num.status === 'active' ? 'active' : 'inactive'}"></span>
                      ${num.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span class="cost-text">$${num.monthlyCost.toFixed(2)}</span>
                  </td>
                  <td>
                    <div class="actions-cell" @click="${(e) => e.stopPropagation()}">
                      <button class="action-btn" title="Edit" @click="${() => this._openEditModal(num)}">
                        <i class="fa-sharp fa-light fa-pen-to-square"></i>
                      </button>
                      <button class="action-btn" title="Settings" @click="${() => this._openEditModal(num)}">
                        <i class="fa-sharp fa-light fa-gear"></i>
                      </button>
                      <button class="action-btn danger" title="Delete" @click="${() => this._confirmDeleteNumber(num)}">
                        <i class="fa-sharp fa-light fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                ${this.selectedNumber?.id === num.id ? html`
                  <tr class="expand-row">
                    <td colspan="7">
                      <div class="expand-content">
                        <div class="expand-grid">
                          <!-- Inbound Hours -->
                          <div class="expand-section">
                            <span class="expand-section-title">
                              <i class="fa-sharp fa-light fa-clock"></i>
                              Inbound Hours
                            </span>
                            ${num.inboundHours.length > 0 ? html`
                              <div class="hours-list">
                                ${num.inboundHours.map(h => html`
                                  <div class="hours-item">
                                    <span class="hours-day">${h.day}</span>
                                    <span class="hours-time">${h.time}</span>
                                  </div>
                                `)}
                              </div>
                            ` : html`
                              <span class="expand-section-value" style="color: var(--color-text-tertiary);">
                                No hours configured
                              </span>
                            `}
                          </div>

                          <!-- Caller ID Settings -->
                          <div class="expand-section">
                            <span class="expand-section-title">
                              <i class="fa-sharp fa-light fa-id-card"></i>
                              Caller ID Settings
                            </span>
                            <div style="display: flex; flex-direction: column; gap: var(--space-2);">
                              <div class="caller-id-row">
                                ${num.callerIdEnabled
                                  ? html`<i class="fa-sharp fa-light fa-circle-check"></i> Enabled`
                                  : html`<i class="fa-sharp fa-light fa-circle-xmark" style="color: var(--color-text-disabled);"></i> Disabled`
                                }
                              </div>
                              ${num.callerIdName ? html`
                                <span class="expand-section-value">
                                  Display Name: <strong>${num.callerIdName}</strong>
                                </span>
                              ` : ''}
                              <span class="expand-section-value" style="color: var(--color-text-tertiary); font-size: var(--text-xs);">
                                Routing: ${num.routingRules || 'Not configured'}
                              </span>
                            </div>
                          </div>

                          <!-- Notes -->
                          <div class="expand-section">
                            <span class="expand-section-title">
                              <i class="fa-sharp fa-light fa-note-sticky"></i>
                              Notes
                            </span>
                            <textarea
                              class="notes-textarea"
                              .value="${num.notes}"
                              placeholder="Add notes about this number..."
                              @click="${(e) => e.stopPropagation()}"
                              @blur="${(e) => this._saveNotes(num, e)}"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ` : ''}
              `)}
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        ${this._totalItems > 0 ? html`
          <div class="pagination-bar">
            <div class="pagination-info">
              Showing <strong>${startIdx + 1}-${endIdx}</strong> of <strong>${this._totalItems.toLocaleString()}</strong>
            </div>
            <div class="pagination-controls">
              <button class="page-btn" ?disabled="${this._currentPage <= 1}" @click="${() => this._goToPage(this._currentPage - 1)}">
                <i class="fa-sharp fa-light fa-chevron-left"></i>
              </button>
              ${this._renderPageNumbers()}
              <button class="page-btn" ?disabled="${this._currentPage >= this._totalPages}" @click="${() => this._goToPage(this._currentPage + 1)}">
                <i class="fa-sharp fa-light fa-chevron-right"></i>
              </button>
            </div>
          </div>
        ` : ''}
      </div>

      <!-- Add/Edit Modal -->
      ${this._showModal ? this._renderModal() : ''}

      <!-- Delete Confirmation -->
      ${this._confirmDelete ? html`
        <div class="confirm-overlay" @click=${this._cancelDelete}>
          <div class="confirm-box" @click=${(e) => e.stopPropagation()}>
            <div class="confirm-icon">
              <i class="fa-sharp fa-light fa-trash"></i>
            </div>
            <div class="confirm-title">Delete Phone Number</div>
            <div class="confirm-message">
              Are you sure you want to delete <strong>${this._confirmDelete.phone}</strong>
              ${this._confirmDelete.label ? `(${this._confirmDelete.label})` : ''}?
              This action cannot be undone.
            </div>
            <div class="confirm-actions">
              <button class="btn-cancel" @click=${this._cancelDelete}>Cancel</button>
              <button class="btn-danger" @click=${this._executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      ` : ''}

      <!-- Toast -->
      ${this._toastVisible ? html`
        <div class="toast ${this._toastType}">
          <i class="fa-sharp fa-light ${this._toastType === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}"></i>
          ${this._toastMessage}
        </div>
      ` : ''}
    `;
  }

  /* ─── Modal Render ─── */

  _renderModal() {
    const d = this._editingNumber;
    const isEdit = this._modalMode === 'edit';

    return html`
      <div class="modal-overlay" @click=${this._closeModal}>
        <div class="modal" @click=${(e) => e.stopPropagation()}>
          <div class="modal-header">
            <span class="modal-title">${isEdit ? 'Edit Phone Number' : 'Add Phone Number'}</span>
            <button class="modal-close" @click=${this._closeModal}>
              <i class="fa-sharp fa-light fa-xmark"></i>
            </button>
          </div>

          <div class="modal-body">
            <!-- Basic Info -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Phone Number *</label>
                <input type="text" class="form-input" placeholder="+15551234567"
                  .value=${d.twilio_number || ''}
                  @input=${(e) => this._updateField('twilio_number', e.target.value)}
                  style="font-family: var(--font-mono);"
                />
                <span class="form-hint">E.164 format</span>
              </div>
              <div class="form-group">
                <label class="form-label">Label</label>
                <input type="text" class="form-input" placeholder="Main Reservations"
                  .value=${d.label || ''}
                  @input=${(e) => this._updateField('label', e.target.value)}
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Number Type</label>
                <select class="form-select"
                  .value=${d.number_type || 'Local'}
                  @change=${(e) => this._updateField('number_type', e.target.value)}>
                  <option value="Local">Local</option>
                  <option value="Toll-Free">Toll-Free</option>
                  <option value="Mobile">Mobile</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Country</label>
                <select class="form-select"
                  .value=${d.country_code || 'US'}
                  @change=${(e) => this._updateField('country_code', e.target.value)}>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="MX">Mexico</option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Agent Name</label>
                <input type="text" class="form-input" placeholder="Reservation Assistant"
                  .value=${d.agent_name || ''}
                  @input=${(e) => this._updateField('agent_name', e.target.value)}
                />
              </div>
              <div class="form-group">
                <label class="form-label">Status</label>
                <select class="form-select"
                  .value=${d.status || 'inactive'}
                  @change=${(e) => this._updateField('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <!-- Park / Cost -->
            <div class="form-section-title">Park & Billing</div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">UJID (Park ID)</label>
                <input type="number" class="form-input" placeholder="4821"
                  .value=${d.ujid || ''}
                  @input=${(e) => this._updateField('ujid', e.target.value)}
                />
              </div>
              <div class="form-group">
                <label class="form-label">Park Name</label>
                <input type="text" class="form-input" placeholder="My RV Park"
                  .value=${d.park_name || ''}
                  @input=${(e) => this._updateField('park_name', e.target.value)}
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Monthly Cost ($)</label>
                <input type="number" step="0.01" class="form-input" placeholder="4.50"
                  .value=${d.monthly_cost || ''}
                  @input=${(e) => this._updateField('monthly_cost', e.target.value)}
                  style="font-family: var(--font-mono);"
                />
              </div>
              <div class="form-group">
                <label class="form-label">Timezone</label>
                <select class="form-select"
                  .value=${d.timezone || 'America/New_York'}
                  @change=${(e) => this._updateField('timezone', e.target.value)}>
                  <option value="America/New_York">Eastern (ET)</option>
                  <option value="America/Chicago">Central (CT)</option>
                  <option value="America/Denver">Mountain (MT)</option>
                  <option value="America/Los_Angeles">Pacific (PT)</option>
                  <option value="America/Anchorage">Alaska (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii (HT)</option>
                </select>
              </div>
            </div>

            <!-- Caller ID & Routing -->
            <div class="form-section-title">Caller ID & Routing</div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Caller ID Name</label>
                <input type="text" class="form-input" placeholder="My Business Name"
                  .value=${d.caller_id_name || ''}
                  @input=${(e) => this._updateField('caller_id_name', e.target.value)}
                />
              </div>
              <div class="form-group">
                <label class="form-label">Caller ID</label>
                <div class="toggle-row" style="margin-top: var(--space-1);">
                  <div class="toggle ${d.caller_id_enabled ? 'active' : ''}"
                    @click=${() => this._updateField('caller_id_enabled', !d.caller_id_enabled)}
                  ></div>
                  <span style="font-size: var(--text-sm); color: var(--color-text-secondary);">
                    ${d.caller_id_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            <div class="form-group full">
              <label class="form-label">Routing Rules</label>
              <input type="text" class="form-input" placeholder="Default routing to agent"
                .value=${d.routing_rules || ''}
                @input=${(e) => this._updateField('routing_rules', e.target.value)}
              />
            </div>

            <div class="form-group full">
              <label class="form-label">Notes</label>
              <textarea class="form-textarea" placeholder="Internal notes about this number..."
                .value=${d.notes || ''}
                @input=${(e) => this._updateField('notes', e.target.value)}
              ></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click=${this._closeModal}>Cancel</button>
            <button class="btn btn-primary" @click=${this._saveModal} ?disabled=${this._saving}>
              ${this._saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Number')}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('phone-numbers', PhoneNumbers);

